import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { mapFilterObjectToPayload } from './mappings';
import type {
    BaseRequest,
    ContentItem,
    ContentPublish,
    ContentRequest,
    ContentResponse,
    ContentTranslate,
    EnvResponse,
    JobStatusRequest,
    JobStatusResponse,
    LanguagesRequest,
    LanguagesResponse,
    QueueResponse,
} from './types';
import { type GroupedItems } from './types';

const authHeader = (token: string): Record<string, string> => ({
    Authorization: `Bearer ${token}`,
});

const baseQuery = () =>
    fetchBaseQuery({
        baseUrl: '/',
        prepareHeaders: (headers) => headers,
    });

const getHost = (host: string) => (process.env.NODE_ENV !== 'test' ? host : 'http://localhost');

const contentEngineApi = createApi({
    reducerPath: 'content_engine_api',
    baseQuery: baseQuery(),
    tagTypes: ['env'],
    endpoints: (builder) => ({
        getEnv: builder.query<EnvResponse, BaseRequest>({
            query: ({ host, token }) => ({
                method: 'GET',
                headers: authHeader(token),
                url: `${getHost(host)}/env`,
            }),
            providesTags: ['env'],
        }),
        getContent: builder.query<ContentResponse<GroupedItems<ContentItem>>, ContentRequest>({
            query: ({ host, token, ...payload }) => ({
                method: 'POST',
                headers: authHeader(token),
                url: `${getHost(host)}/content`,
                body: {
                    page: payload.page ?? 1,
                    limit: payload.limit ?? 25,
                    searchQuery: payload.searchQuery ?? '',
                    filters: payload.filters ? mapFilterObjectToPayload(payload.filters) : [],
                    status: payload.status ?? ['Untranslated'],
                },
            }),
            keepUnusedDataFor: 0,
        }),
        getTranslationInProgressContentCount: builder.query<number, ContentRequest>({
            query: ({ host, token }) => ({
                method: 'POST',
                headers: authHeader(token),
                url: `${getHost(host)}/content`,
                body: {
                    page: 1,
                    limit: 1,
                    filters: [],
                    status: ['InProgress', 'Completed', 'Published'],
                },
            }),
            transformResponse: (data: ContentResponse<ContentItem>) => data.total,
            keepUnusedDataFor: 0,
        }),
        getJobStatus: builder.query<JobStatusResponse, JobStatusRequest>({
            query: ({ host, token, jobId }) => ({
                method: 'GET',
                headers: authHeader(token),
                url: `${getHost(host)}/status/jobs/${jobId}`,
            }),
            keepUnusedDataFor: 0,
        }),
        postLanguages: builder.mutation<LanguagesResponse, LanguagesRequest>({
            query: ({ host, token, data }) => ({
                url: `${getHost(host)}/languages`,
                headers: authHeader(token),
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['env'],
        }),
        publishContent: builder.mutation<QueueResponse, { host: string; token: string; data: ContentPublish }>({
            query: ({ host, token, data }) => ({
                url: `${getHost(host)}/content/publish`,
                headers: authHeader(token),
                method: 'POST',
                body: data,
            }),
        }),
        refreshContent: builder.mutation<QueueResponse, BaseRequest>({
            query: ({ host, token }) => ({
                url: `${getHost(host)}/content/refresh`,
                headers: authHeader(token),
                method: 'POST',
            }),
        }),
        translateContent: builder.mutation<QueueResponse, { host: string; token: string; data: ContentTranslate }>({
            query: ({ host, token, data }) => ({
                url: `${getHost(host)}/content/translate`,
                headers: authHeader(token),
                method: 'POST',
                body: data,
            }),
        }),
    }),
});

export const {
    useGetEnvQuery,
    useGetContentQuery,
    useGetTranslationInProgressContentCountQuery,
    useGetJobStatusQuery,
    useLazyGetContentQuery,
    usePostLanguagesMutation,
    useRefreshContentMutation,
    useTranslateContentMutation,
    usePublishContentMutation,
} = contentEngineApi;

export * from './types';
export * from './mappings';
export default contentEngineApi;
